import moment from 'moment';
import React from 'react';
import { UiTrashAlt } from '@vyaguta/vyaguta-icons/ui';

import Tooltip from 'components/common/tooltip';
import DropDown from 'components/common/dropDown';
import InputWrapper from 'components/common/inputWrapper';
import DatePicker from 'components/common/datepicker/DatePicker';

import { isObjectEmpty } from 'utils/object';
import { getFormattedDate } from 'utils/date';

import { FIXED_TERM_CONTRACT } from 'constants/employeeStatus';
import { EMPLOYEE_STATUS_PROBATION_DETAILS_VALUES } from 'constants/employeeStatusDetails';
import type { DropdownOption } from 'components/common/dropDown/Dropdown';

interface EmployeeStatusDetailsItem {
  keyId: number;
  category: string;
  date?: string;
  id?: number;
}

interface AddEmployeeStatusDetailsProps {
  item: EmployeeStatusDetailsItem;
  setEmpStatusDetails: React.Dispatch<React.SetStateAction<EmployeeStatusDetailsItem[]>>;
  handleDelete: () => void;
  values: { engagementStatusId?: number; transitionDate: moment.Moment | string | Date };
  employee: { currentTransitionDate: string };
  currentFiscalYear: { startDate: string };
  type: string;
  index: number;
  touched: { empStatusDetails?: Array<{ category?: boolean; date?: boolean }> };
  errors: { empStatusDetails?: Array<{ category?: string; date?: string }> };
  submitCount?: number;
}

const AddEmployeeStatusDetails: React.FC<AddEmployeeStatusDetailsProps> = ({
  item,
  setEmpStatusDetails,
  handleDelete,
  values,
  employee,
  currentFiscalYear,
  type,
  index,
  touched,
  errors,
  submitCount,
}) => {
  const showCategoryError = Boolean(
    errors.empStatusDetails?.[index]?.category && (submitCount || touched.empStatusDetails?.[index]?.category)
  );
  const showDateError = Boolean(
    errors.empStatusDetails?.[index]?.date && (submitCount || touched.empStatusDetails?.[index]?.date)
  );

  const getUpdatedData = (
    prev: EmployeeStatusDetailsItem[],
    key: 'category' | 'date',
    value: string
  ): EmployeeStatusDetailsItem[] => {
    const newData = prev.map((data) => (data.keyId === item.keyId ? { ...data, [key]: value } : data));

    return newData;
  };

  const onDropDownChange = (data: DropdownOption | DropdownOption[] | null) => {
    if (!data || Array.isArray(data)) {
      return;
    }

    const { value } = data;
    setEmpStatusDetails((prev) => getUpdatedData(prev, 'category', String(value)));
  };

  const onDateChange = (value: moment.Moment | string | Date) => {
    setEmpStatusDetails((prev) => getUpdatedData(prev, 'date', getFormattedDate(value)));
  };

  const getDefaultValue = (currentItem: EmployeeStatusDetailsItem) => {
    if (!currentItem.category || isObjectEmpty({ value: currentItem.category })) {
      return null;
    }

    return {
      label: currentItem.category,
      value: currentItem.category,
    };
  };

  return (
    <div className="emp-status-details-form__category">
      <div className="d-flex">
        <InputWrapper
          label="Category"
          className="emp-status-details-form__input emp-status-details-form__input--lg"
          error={showCategoryError ? errors.empStatusDetails?.[index]?.category : ''}
          isMandatory
        >
          <DropDown
            defaultValue={getDefaultValue(item)}
            onDropDownChange={onDropDownChange}
            options={EMPLOYEE_STATUS_PROBATION_DETAILS_VALUES}
            isSearchable={true}
            isDisabled={type === FIXED_TERM_CONTRACT}
            customClassName="employee-form__dropdown"
            placeholder={'Select...'}
            hasError={showCategoryError}
          />
        </InputWrapper>

        <InputWrapper
          label="Date"
          className="employee-form__date emp-status-details-form__input emp-status-details-form__input--lg"
          error={showDateError ? errors.empStatusDetails?.[index]?.date : ''}
          isMandatory
        >
          <DatePicker
            date={item.date ? moment(item.date) : null}
            onDateChange={onDateChange}
            isOutsideRange={(date) =>
              date.isBefore(employee.currentTransitionDate) ||
              date.isBefore(currentFiscalYear.startDate) ||
              date.isBefore(values.transitionDate)
            }
            hasError={showDateError}
          />
        </InputWrapper>
        <div className="emp-status-details-form__delete emp-status-details-form__delete" onClick={handleDelete}>
          <Tooltip
            html={
              <div className="tooltip-info">
                <p className="tooltip-info__title">Delete</p>
              </div>
            }
          >
            <div className="icon-container">
              <UiTrashAlt />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="emp-status-details-form__divider"></div>
    </div>
  );
};

export default AddEmployeeStatusDetails;
